import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { TagProps } from 'antd/lib/tag';
import { ColorNameType } from '../theme';
import AddTag from './AddTag';
import TypeTag from './TypeTag';

export type TagGroupOption<T extends string = string> = {
  label: string;
  value: T;
};

export interface TagGroupProps<T extends string = string> {
  options: TagGroupOption<T>[];
  closeButtonColor?: ColorNameType;
  tagProps?: TagProps;
  onChange: (tags: TagGroupOption<T>[]) => void;
  tags?: TagGroupOption<T>[];
  limit?: number;
  disabled?: boolean;
}

const TagGroup = <T extends string = string>({
  options,
  closeButtonColor,
  tagProps,
  onChange,
  tags = [],
  limit,
  disabled,
}: TagGroupProps<T>) => {
  const setTags = useCallback(
    (newTags: TagGroupOption<T>[]) => {
      onChange(newTags.filter(tag => !!tag?.value));
    },
    [onChange],
  );

  const removeTag = useCallback(
    (index: number) => {
      const newTags = tags.slice();
      newTags.splice(index, 1);
      setTags(newTags);
    },
    [tags, setTags],
  );

  const addTag = useCallback(
    (value: string) => {
      const tag = options.find(option => option.value === value);
      if (tag) {
        setTags([...tags, tag]);
      }
    },
    [options, tags, setTags],
  );

  const availableOptions = useMemo(
    () =>
      options.filter(option => !tags.some(tag => tag?.value === option.value)),
    [options, tags],
  );

  const showAddButton =
    !disabled && (!limit || tags.length < limit) && availableOptions.length > 0;

  return (
    <Container>
      {tags.map(
        (tag, index) =>
          tag && (
            <TypeTag
              key={tag.value}
              {...tag}
              {...tagProps}
              disabled={disabled}
              closeButtonColor={closeButtonColor}
              onClose={() => removeTag(index)}
            />
          ),
      )}
      {showAddButton && (
        <AddTag onSelectOption={addTag} options={availableOptions} />
      )}
    </Container>
  );
};

export default TagGroup;

const Container = styled.div`
  flex: 1;
`;
